import { useState, useEffect, useCallback, useMemo } from "react";
import { Skeleton } from "@app/components/shared/Skeleton";
import { logger } from "@app/services/logger";
import {
  getConfidenceCalibration,
  getWinRateByRegime,
  type ConfidenceCalibrationResponse,
  type WinRateByRegimeResponse,
} from "../services/performanceApi";

const winRateFill = (rate: number): React.CSSProperties =>
  ({
    height: "100%",
    borderRadius: "3px",
    width: `${Math.min(rate, 100)}%`,
    background:
      rate >= 60 ? "#22c55e" : rate >= 45 ? "#eab308" : "#ef4444",
    transition: "width 0.5s ease",
  }) as React.CSSProperties;

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  section: {
    background: "rgba(30, 41, 59, 0.5)",
    borderRadius: "12px",
    border: "1px solid rgba(148, 163, 184, 0.1)",
    padding: "20px",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  sectionTitle: {
    fontSize: "1.05rem",
    fontWeight: 600,
    color: "#e2e8f0",
    margin: 0,
  },
  sectionSubtitle: {
    fontSize: "0.8rem",
    color: "rgba(203, 213, 225, 0.6)",
    marginTop: "4px",
  },
  controls: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  select: {
    background: "rgba(15, 23, 42, 0.6)",
    border: "1px solid rgba(148, 163, 184, 0.2)",
    borderRadius: "6px",
    color: "#e2e8f0",
    padding: "6px 12px",
    fontSize: "0.85rem",
    cursor: "pointer",
  },
  statsRow: {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap" as const,
    marginBottom: "16px",
  },
  statCard: {
    background: "rgba(15, 23, 42, 0.4)",
    borderRadius: "8px",
    padding: "12px 16px",
    minWidth: "120px",
    flex: 1,
  },
  statLabel: {
    fontSize: "0.75rem",
    color: "rgba(203, 213, 225, 0.6)",
    fontWeight: 500,
    marginBottom: "4px",
  },
  statValue: {
    fontSize: "1.3rem",
    fontWeight: 700,
    color: "#e2e8f0",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
    fontSize: "0.85rem",
  },
  th: {
    textAlign: "left" as const,
    padding: "10px 12px",
    borderBottom: "1px solid rgba(148, 163, 184, 0.15)",
    color: "rgba(203, 213, 225, 0.7)",
    fontWeight: 600,
    fontSize: "0.78rem",
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    whiteSpace: "nowrap" as const,
  },
  td: {
    padding: "10px 12px",
    borderBottom: "1px solid rgba(148, 163, 184, 0.08)",
    color: "#cbd5e1",
    whiteSpace: "nowrap" as const,
  },
  winRateBar: {
    height: "6px",
    borderRadius: "3px",
    background: "rgba(148, 163, 184, 0.15)",
    overflow: "hidden" as const,
    minWidth: "80px",
  },
  emptyState: {
    textAlign: "center" as const,
    padding: "40px 20px",
    color: "rgba(203, 213, 225, 0.5)",
    fontSize: "0.9rem",
  },
  errorState: {
    textAlign: "center" as const,
    padding: "24px",
    color: "#ef4444",
  },
  retryButton: {
    marginTop: "12px",
    padding: "8px 16px",
    background: "rgba(239, 68, 68, 0.15)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    borderRadius: "6px",
    color: "#ef4444",
    cursor: "pointer",
    fontSize: "0.85rem",
  },
};

function SkeletonTable({ rows = 5, cols = 6 }: { rows?: number; cols?: number }): JSX.Element {
  return (
    <div>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            gap: "12px",
            padding: "10px 0",
            borderBottom: "1px solid rgba(148, 163, 184, 0.08)",
          }}
        >
          {Array.from({ length: cols }).map((_, j) => (
            <Skeleton
              key={j}
              height="16px"
              width={`${60 + Math.random() * 80}px`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function getWinRateColor(rate: number): string {
  if (rate >= 60) return "#22c55e";
  if (rate >= 45) return "#eab308";
  return "#ef4444";
}

function getConfidenceColor(conf: number): string {
  if (conf >= 0.7) return "#22c55e";
  if (conf >= 0.5) return "#eab308";
  return "#ef4444";
}

interface PerformanceAnalyticsProps {
  isVisible: boolean;
}

export function PerformanceAnalytics({
  isVisible,
}: PerformanceAnalyticsProps): JSX.Element | null {
  const [days, setDays] = useState(90);

  const [calibration, setCalibration] =
    useState<ConfidenceCalibrationResponse | null>(null);
  const [calibrationLoading, setCalibrationLoading] = useState(false);
  const [calibrationError, setCalibrationError] = useState<string | null>(null);

  const [regimeData, setRegimeData] = useState<WinRateByRegimeResponse | null>(
    null
  );
  const [regimeLoading, setRegimeLoading] = useState(false);
  const [regimeError, setRegimeError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setCalibrationLoading(true);
    setRegimeLoading(true);
    setCalibrationError(null);
    setRegimeError(null);

    try {
      const [calib, regime] = await Promise.all([
        getConfidenceCalibration(days),
        getWinRateByRegime(days),
      ]);
      setCalibration(calib);
      setRegimeData(regime);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      const msg = error.message || "Failed to load performance data";
      logger.error("Error loading performance analytics", error);
      setCalibrationError(msg);
      setRegimeError(msg);
    } finally {
      setCalibrationLoading(false);
      setRegimeLoading(false);
    }
  }, [days]);

  useEffect(() => {
    if (isVisible) {
      loadData();
    }
  }, [isVisible, loadData]);

  if (!isVisible) {
    return null;
  }

  const periodLabel = `${days}-Day Analysis`;

  function renderCalibrationSection(): JSX.Element {
    if (calibrationLoading) {
      return (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>Confidence Calibration</h3>
          </div>
          <SkeletonTable rows={5} cols={4} />
        </div>
      );
    }

    if (calibrationError) {
      return (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>Confidence Calibration</h3>
          </div>
          <div style={styles.errorState}>
            {calibrationError}
            <br />
            <button
              type="button"
              style={styles.retryButton}
              onClick={loadData}
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    const hasData =
      calibration && calibration.buckets && calibration.buckets.length > 0;

    if (!hasData) {
      return (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>Confidence Calibration</h3>
          </div>
          <div style={styles.emptyState}>
            No trade outcome data available for the selected period.
            <br />
            Trades must be executed and synced before calibration can be
            calculated.
          </div>
        </div>
      );
    }

    return (
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <h3 style={styles.sectionTitle}>Confidence Calibration</h3>
            <div style={styles.sectionSubtitle}>
              Validates that higher-confidence recommendations yield higher win
              rates
            </div>
          </div>
          <div style={styles.statLabel}>
            {calibration!.total_trades_analyzed} trades analyzed
          </div>
        </div>

        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Total Trades</div>
            <div style={styles.statValue}>
              {calibration!.total_trades_analyzed}
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Period</div>
            <div style={{ ...styles.statValue, fontSize: "1rem" }}>
              {periodLabel}
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Confidence Buckets</div>
            <div style={styles.statValue}>{calibration!.buckets.length}</div>
          </div>
        </div>

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Confidence Range</th>
              <th style={styles.th}>Trades</th>
              <th style={styles.th}>Wins</th>
              <th style={styles.th}>Losses</th>
              <th style={styles.th}>BE</th>
              <th style={styles.th}>Win Rate</th>
              <th style={styles.th}>Avg PnL %</th>
            </tr>
          </thead>
          <tbody>
            {calibration!.buckets.map((b) => (
              <tr key={b.confidence_range}>
                <td style={styles.td}>
                  <span
                    style={{
                      color: getConfidenceColor(b.min_confidence),
                      fontWeight: 600,
                    }}
                  >
                    {b.confidence_range}
                  </span>
                </td>
                <td style={styles.td}>{b.total_trades}</td>
                <td style={{ ...styles.td, color: "#22c55e" }}>
                  {b.win_count}
                </td>
                <td style={{ ...styles.td, color: "#ef4444" }}>
                  {b.loss_count}
                </td>
                <td style={styles.td}>{b.breakeven_count}</td>
                <td style={styles.td}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div style={styles.winRateBar}>
                      <div style={winRateFill(b.win_rate)} />
                    </div>
                    <span
                      style={{
                        color: getWinRateColor(b.win_rate),
                        fontWeight: 600,
                        fontSize: "0.82rem",
                      }}
                    >
                      {b.win_rate.toFixed(1)}%
                    </span>
                  </div>
                </td>
                <td
                  style={{
                    ...styles.td,
                    color: b.avg_pnl_pct >= 0 ? "#22c55e" : "#ef4444",
                    fontWeight: 500,
                  }}
                >
                  {b.avg_pnl_pct >= 0 ? "+" : ""}
                  {b.avg_pnl_pct.toFixed(2)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderRegimeSection(): JSX.Element {
    if (regimeLoading) {
      return (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>Win Rate by Regime</h3>
          </div>
          <SkeletonTable rows={5} cols={5} />
        </div>
      );
    }

    if (regimeError) {
      return (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>Win Rate by Regime</h3>
          </div>
          <div style={styles.errorState}>
            {regimeError}
            <br />
            <button
              type="button"
              style={styles.retryButton}
              onClick={loadData}
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    const hasData =
      regimeData && regimeData.regimes && regimeData.regimes.length > 0;

    if (!hasData) {
      return (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>Win Rate by Regime</h3>
          </div>
          <div style={styles.emptyState}>
            No regime data available for the selected period.
          </div>
        </div>
      );
    }

    return (
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <h3 style={styles.sectionTitle}>Win Rate by Regime</h3>
            <div style={styles.sectionSubtitle}>
              Performance breakdown by symbol and direction
            </div>
          </div>
        </div>

        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Overall Win Rate</div>
            <div
              style={{
                ...styles.statValue,
                color: getWinRateColor(regimeData!.overall_win_rate),
              }}
            >
              {regimeData!.overall_win_rate.toFixed(1)}%
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Total Trades</div>
            <div style={styles.statValue}>
              {regimeData!.total_trades_analyzed}
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Active Regimes</div>
            <div style={styles.statValue}>{regimeData!.regimes.length}</div>
          </div>
        </div>

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Symbol</th>
              <th style={styles.th}>Direction</th>
              <th style={styles.th}>Trades</th>
              <th style={styles.th}>Wins</th>
              <th style={styles.th}>Losses</th>
              <th style={styles.th}>Win Rate</th>
              <th style={styles.th}>Avg PnL %</th>
              <th style={styles.th}>Total PnL</th>
              <th style={styles.th}>Avg Confidence</th>
            </tr>
          </thead>
          <tbody>
            {regimeData!.regimes.map((r) => (
              <tr key={`${r.symbol}-${r.direction}`}>
                <td
                  style={{
                    ...styles.td,
                    fontWeight: 600,
                    color: "#e2e8f0",
                  }}
                >
                  {r.symbol}
                </td>
                <td style={styles.td}>
                  <span
                    style={{
                      color: r.direction === "long" ? "#22c55e" : "#ef4444",
                      fontWeight: 500,
                    }}
                  >
                    {r.direction.toUpperCase()}
                  </span>
                </td>
                <td style={styles.td}>{r.total_trades}</td>
                <td style={{ ...styles.td, color: "#22c55e" }}>
                  {r.win_count}
                </td>
                <td style={{ ...styles.td, color: "#ef4444" }}>
                  {r.loss_count}
                </td>
                <td style={styles.td}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div style={styles.winRateBar}>
                      <div style={winRateFill(r.win_rate)} />
                    </div>
                    <span
                      style={{
                        color: getWinRateColor(r.win_rate),
                        fontWeight: 600,
                        fontSize: "0.82rem",
                      }}
                    >
                      {r.win_rate.toFixed(1)}%
                    </span>
                  </div>
                </td>
                <td
                  style={{
                    ...styles.td,
                    color: r.avg_pnl_pct >= 0 ? "#22c55e" : "#ef4444",
                    fontWeight: 500,
                  }}
                >
                  {r.avg_pnl_pct >= 0 ? "+" : ""}
                  {r.avg_pnl_pct.toFixed(2)}%
                </td>
                <td
                  style={{
                    ...styles.td,
                    color: r.total_pnl_usdt >= 0 ? "#22c55e" : "#ef4444",
                    fontWeight: 500,
                  }}
                >
                  {r.total_pnl_usdt >= 0 ? "+" : ""}
                  {r.total_pnl_usdt.toFixed(2)}
                </td>
                <td
                  style={{
                    ...styles.td,
                    color: getConfidenceColor(r.avg_confidence),
                  }}
                >
                  {(r.avg_confidence * 100).toFixed(0)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <span style={{ color: "rgba(203, 213, 225, 0.6)", fontSize: "0.85rem" }}>
          Analysis Period:
        </span>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          style={styles.select}
        >
          <option value={7}>7 Days</option>
          <option value={30}>30 Days</option>
          <option value={90}>90 Days</option>
          <option value={180}>180 Days</option>
          <option value={365}>365 Days</option>
        </select>
      </div>

      {renderCalibrationSection()}
      {renderRegimeSection()}
    </div>
  );
}
